import {
  differenceInDays,
  differenceInMilliseconds,
  format,
  formatDistanceToNow,
  isToday,
  isYesterday,
  intervalToDuration,
} from 'date-fns';
import type { Duration } from 'date-fns';
import ar from 'date-fns/locale/ar';
import az from 'date-fns/locale/az';
import de from 'date-fns/locale/de';
import enUS from 'date-fns/locale/en-US';
import es from 'date-fns/locale/es';
import fr from 'date-fns/locale/fr';
import hi from 'date-fns/locale/hi';
import id from 'date-fns/locale/id';
import it from 'date-fns/locale/it';
import ja from 'date-fns/locale/ja';
import ko from 'date-fns/locale/ko';
import nl from 'date-fns/locale/nl';
import pt from 'date-fns/locale/pt';
import ptBR from 'date-fns/locale/pt-BR';
import sv from 'date-fns/locale/sv';
import th from 'date-fns/locale/th';
import tr from 'date-fns/locale/tr';
import vi from 'date-fns/locale/vi';
import zhCN from 'date-fns/locale/zh-CN';
import zhTW from 'date-fns/locale/zh-TW';
import { i18n } from 'next-i18next';

import { SupportLanguage } from 'src/types/Language';

import { getLocale } from './language';
import { DAY_MS } from './time';

type DurationUnitType = keyof Duration;

export const languageDateLocaleMap: Record<SupportLanguage, Locale> = {
  ar,
  az,
  de,
  en: enUS,
  es,
  'es-AR': es,
  fr,
  hi,
  id,
  it,
  ja,
  ko,
  tr,
  'zh-CN': zhCN,
  'zh-TW': zhTW,
  th,
  sv,
  vi,
  nl,
  pt,
  'pt-BR': ptBR,
};

export const checkAgeOverN = (birthday: Date, n: number, today: Date = new Date()) => {
  const dateNYearsAgo = new Date(today);
  dateNYearsAgo.setFullYear(dateNYearsAgo.getFullYear() - n);
  dateNYearsAgo.setHours(0, 0, 0, 0);

  const birthdayZeroTime = new Date(birthday);
  birthdayZeroTime.setHours(0, 0, 0, 0);

  return birthdayZeroTime.getTime() <= dateNYearsAgo.getTime();
};

export const formatDate = (date: Date | number) => {
  let tmp = new Date(date);
  const offset = tmp.getTimezoneOffset();
  tmp = new Date(tmp.getTime() - offset * 60 * 1000);
  const [isoDate] = tmp.toISOString().split('T');
  return isoDate;
};

export const formatDateDistance = (date: Date) => {
  const d = new Date(date);
  const now = Date.now();

  // dynamic import로 하면 bundle size가 늘어남
  const locale = languageDateLocaleMap[(i18n?.language as SupportLanguage) || 'en'];

  if (differenceInDays(now, d) < 7) {
    const dateTimeString =
      ' ' +
      date.toLocaleTimeString(locale.code, {
        hour: 'numeric',
        minute: 'numeric',
      });
    if (isToday(d)) {
      return i18n ? i18n?.t('history_time_today') + dateTimeString : '';
    }
    if (isYesterday(d)) {
      return i18n ? i18n?.t('history_time_yesterday') + dateTimeString : '';
    }
    return formatDistanceToNow(d, { addSuffix: true, locale }) + dateTimeString;
  }

  return date.toLocaleTimeString(locale.code, {
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
  });
};

export const getLocaleTimeString = (date: Date, timeZone?: string) => {
  const locale = languageDateLocaleMap[getLocale()];

  return date.toLocaleTimeString(locale.code, {
    timeZone,
    hour: 'numeric',
    minute: 'numeric',
  });
};

export const millisecondsToHHMMSS = (milliseconds: number) => {
  const updateTime = Math.abs(milliseconds);
  const hours = Math.floor(updateTime / (1000 * 60 * 60));
  const minutes = Math.floor((updateTime % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((updateTime % (1000 * 60)) / 1000);

  const formattedHours = String(hours).padStart(2, '0');
  const formattedMinutes = String(minutes).padStart(2, '0');
  const formattedSeconds = String(seconds).padStart(2, '0');

  return `${formattedHours}:${formattedMinutes}:${formattedSeconds}`;
};

export type DatePatternShorthand = 'MM' | 'DD' | 'YEAR';

/**
 * @returns Locale에 따라 정렬된 ['MM', 'DD', 'YEAR'] 형태의 날짜 패턴을 반환
 * @example ko = ['YEAR', 'MM', 'DD']
 * @example en = ['MM', 'DD', 'YEAR']
 */
export const getLocaleDatePattern = (): DatePatternShorthand[] => {
  const options = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  } as const;
  const locale = navigator.language;
  const formatter = new Intl.DateTimeFormat(locale, options).formatToParts();

  const result = formatter
    .map(function (e) {
      switch (e.type) {
        case 'month':
          return 'MM';
        case 'day':
          return 'DD';
        case 'year':
          return 'YEAR';
        default:
          return null;
      }
    })
    .filter((e) => e !== null);

  return result as DatePatternShorthand[];
};

export const calculateAge = (birthDate: Date) => {
  const today = new Date();

  // 나이 계산
  const yearDiff = today.getFullYear() - birthDate.getFullYear();

  // 생일이 지났는지 확인 (만나이 계산)
  const isBirthdayPassed =
    today.getMonth() > birthDate.getMonth() ||
    (today.getMonth() === birthDate.getMonth() && today.getDate() >= birthDate.getDate());

  if (!isBirthdayPassed) {
    return yearDiff - 1;
  }

  return yearDiff;
};

export function formatTimestamp(timestamp: number) {
  const now = new Date();
  const dateA = new Date(timestamp);

  // 두 날짜의 차이를 밀리초 단위로 계산
  const diffInMs = Math.abs(differenceInMilliseconds(now, dateA));

  if (diffInMs < DAY_MS) {
    // 하루 이하 차이: 'hh:mm a' 형식으로 포맷팅
    return format(dateA, 'hh:mm a', { locale: languageDateLocaleMap[getLocale()] }); // 예: '03:45 PM'
  } else {
    // 하루 이상 차이: 'yyyy.MM.dd' 형식으로 포맷팅
    return format(dateA, 'yyyy.MM.dd', { locale: languageDateLocaleMap[getLocale()] }); // 예: '2025.02.10'
  }
}

/**
 * 주어진 타임스탬프와 포맷 문자열을 받아, 현재 로케일에 맞게 날짜를 포맷합니다.
 * @see {@link https://date-fns.org/docs/format}
 * @param timestamp 포맷할 날짜의 타임스탬프 (number)
 * @param formatStr 사용할 날짜 포맷 문자열 (예: 'yyyy.MM.dd')
 * @returns 로케일에 맞게 포맷된 날짜 문자열
 */
export const formatWithLocale = (timestamp: number, formatStr: string) =>
  format(timestamp, formatStr, { locale: languageDateLocaleMap[getLocale()] });

export const getSatisfiedUnitOfTime = (
  time: number,
  units: DurationUnitType[] = ['days', 'hours', 'minutes', 'seconds']
) => {
  const duration = intervalToDuration({ start: 0, end: time });

  const satisfiedUnit = units.find((unit) => (duration[unit] ?? 0) > 0);

  return (
    satisfiedUnit && {
      unit: satisfiedUnit,
      duration: duration[satisfiedUnit] ?? 0,
    }
  );
};

export const getRemainTimesInfo = (time: number) =>
  getSatisfiedUnitOfTime(new Date().getTime() - time);

/**
 * 두 날짜 값(valueA, valueB)의 차이를 밀리초 단위로 반환합니다.
 *
 * @param valueA - 비교할 첫 번째 날짜 값 (string, number, null, undefined)
 * @param valueB - 비교할 두 번째 날짜 값 (string, number, null, undefined)
 * @returns valueA - valueB의 밀리초 차이 (유효하지 않은 입력 시 Infinity 반환)
 */
/**
 * epoch seconds를 축약형 상대 시간 문자열로 변환
 * @example "3h", "5m", "2d", "1w" (en) / "3시간", "5분", "2일", "1주" (ko)
 */
export const formatNarrowRelativeTime = (epochSeconds: number) => {
  const elapsedMs = Date.now() - epochSeconds * 1000;
  const locale = i18n?.language || 'en';

  const minutes = Math.floor(elapsedMs / 60_000);
  const hours = Math.floor(elapsedMs / 3_600_000);
  const days = Math.floor(elapsedMs / 86_400_000);
  const weeks = Math.floor(days / 7);

  const fmt = (value: number, unit: string) =>
    new Intl.NumberFormat(locale, { style: 'unit', unit, unitDisplay: 'narrow' }).format(value);

  if (weeks >= 1) return fmt(weeks, 'week');
  if (days >= 1) return fmt(days, 'day');
  if (hours >= 1) return fmt(hours, 'hour');
  if (minutes >= 1) return fmt(minutes, 'minute');

  return new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }).format(0, 'second');
};

/**
 * Date를 현재 로케일에 맞는 날짜 표기로 변환
 * @example formatDisplayDate(new Date(2025, 3, 6)) // ko: '2025. 04. 06.', en-US: '04/06/2025'
 */
export function formatDisplayDate(date: Date): string {
  return new Intl.DateTimeFormat(i18n?.language || navigator.language, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
}

export const diffOfDates = (valueA: string | number | Date, valueB: string | number | Date) => {
  const timestampA = new Date(valueA).getTime();
  const timestampB = new Date(valueB).getTime();

  if (Number.isNaN(timestampA) || Number.isNaN(timestampB)) return Infinity;

  return timestampA - timestampB;
};
